<template>
  <!-- -------------------------------------------------------------------------- -->
  <!-- 红包雨渲染容器：ref 获取容器宽度，containerStyle 控制实际可视区域 -->
  <!-- -------------------------------------------------------------------------- -->
  <div ref="rainContainerRef" class="red-envelope-rain" :style="containerStyle">
    <!-- 固定红包池渲染；animationiteration 在每轮动画结束后刷新位置 -->
    <img
      v-for="item in envelopePool"
      :key="item.id"
      :src="redEnvelopeImg"
      class="rain-envelope"
      draggable="false"
      :style="getEnvelopeStyle(item)"
      @animationiteration="handleAnimationIteration"
    />
  </div>
</template>

<script setup>
import redEnvelopeImg from '@/assets/theme/red_envelope/us-dollars.png'

/* -------------------------------------------------------------------------- */
/* Props */
/* -------------------------------------------------------------------------- */
const props = defineProps({
  /**
   * 限制红包雨横向生成范围（视口 px 坐标）。
   * 不传时自动读取 .app-wrapper / #app 的可视宽度；
   * min 为左边界，max 为右边界。
   */
  widthRange: {
    type: Object,
    default: null,
    // 校验 widthRange 结构合法，避免运行时计算出无效范围
    validator: (value) => {
      if (value == null) return true

      return (
        typeof value.min === 'number' && typeof value.max === 'number' && value.max >= value.min
      )
    },
  },
})

/* -------------------------------------------------------------------------- */
/* 响应式状态 */
/* -------------------------------------------------------------------------- */
/** 红包雨容器 DOM 引用，用于读取实际可用宽度 */
const rainContainerRef = ref(null)
/** 固定红包池数据，避免频繁创建和销毁 DOM */
const envelopePool = ref([])
/** 最终计算后的横向生成范围，驱动容器 left / width */
const resolvedWidthRange = ref({ min: 0, max: 0 })

/* -------------------------------------------------------------------------- */
/* 红包雨配置参数（修改时需同步检查 CSS 对应值） */
/* -------------------------------------------------------------------------- */
/** 红包数量，影响密度和性能 */
const ENVELOPE_COUNT = 28
/** 红包宽度，需要和 CSS 中 .rain-envelope width 保持一致 */
const ENVELOPE_WIDTH = 48
/** 单次下落动画时长，需要和 CSS animation-duration 保持一致 */
const FALL_DURATION = 4

/* -------------------------------------------------------------------------- */
/* 运行时变量 */
/* -------------------------------------------------------------------------- */
/** 监听页面容器尺寸变化，窗口缩放时同步更新生成范围 */
let resizeObserver = null
/** resize 节流用 requestAnimationFrame ID */
let resizeRafId = null
/** 避免重复绑定 resize / ResizeObserver 监听 */
let isWidthListenerActive = false

/* -------------------------------------------------------------------------- */
/* 工具函数 */
/* -------------------------------------------------------------------------- */

/** 生成 [min, max) 范围内的随机数 */
const random = (min, max) => Math.random() * (max - min) + min

/** 获取当前红包雨实际可用宽度，优先读容器 DOM，回退到 resolvedWidthRange */
const getContainerWidth = () => {
  const container = rainContainerRef.value
  if (container?.clientWidth) return container.clientWidth

  const { min, max } = resolvedWidthRange.value
  return Math.max(0, max - min)
}

/** 随机生成单个红包的 x 坐标和初始透明度 */
const randomizeEnvelopeProps = () => {
  const containerWidth = getContainerWidth()

  return {
    x: random(0, Math.max(0, containerWidth - ENVELOPE_WIDTH)),
    opacity: random(0.5, 1),
  }
}

/**
 * 初始化固定红包池。
 * 使用负 animationDelay 让红包在页面打开时已分布在不同高度，避免同时从顶部落下。
 */
const initEnvelopePool = () => {
  envelopePool.value = Array.from({ length: ENVELOPE_COUNT }, (_, index) => ({
    id: index,
    animationDelay: -random(0, FALL_DURATION),
    ...randomizeEnvelopeProps(),
  }))
}

/** 将红包数据转换成 img 的内联样式 */
const getEnvelopeStyle = (item) => ({
  left: `${item.x}px`,
  '--start-opacity': item.opacity.toFixed(2),
  animationDelay: `${item.animationDelay}s`,
})

/**
 * 每轮动画结束后，直接修改当前 DOM 的 left 和 CSS 变量。
 * 不更新响应式数据，避免触发 Vue diff，降低每轮循环的性能开销。
 */
const handleAnimationIteration = (event) => {
  const target = event.currentTarget
  const containerWidth = getContainerWidth()
  const x = random(0, Math.max(0, containerWidth - ENVELOPE_WIDTH))
  const opacity = random(0.5, 1).toFixed(2)

  target.style.left = `${x}px`
  target.style.setProperty('--start-opacity', opacity)
}

/** 自动识别 .app-wrapper、#app 或 window.innerWidth，作为默认可视区域 */
const resolveVisiblePageWidthRange = () => {
  const appWrapper = document.querySelector('.app-wrapper')
  if (appWrapper) {
    const rect = appWrapper.getBoundingClientRect()
    return { min: rect.left, max: rect.right }
  }

  const app = document.querySelector('#app')
  if (app) {
    const rect = app.getBoundingClientRect()
    return { min: rect.left, max: rect.right }
  }

  return { min: 0, max: window.innerWidth }
}

/** 根据 props.widthRange 或页面容器重新计算红包生成范围 */
const updateWidthRange = () => {
  resolvedWidthRange.value = props.widthRange
    ? { min: props.widthRange.min, max: props.widthRange.max }
    : resolveVisiblePageWidthRange()
}

/** 使用 requestAnimationFrame 节流 resize，合并同一帧内的多次尺寸变化 */
const throttledUpdateWidthRange = () => {
  if (resizeRafId != null) return

  resizeRafId = requestAnimationFrame(() => {
    resizeRafId = null
    updateWidthRange()
  })
}

/** 清理 resize 节流状态，防止组件卸载后 rAF 回调仍被执行 */
const clearResizeThrottle = () => {
  if (resizeRafId == null) return

  cancelAnimationFrame(resizeRafId)
  resizeRafId = null
}

/** 绑定 resize / ResizeObserver 监听，仅在未激活时注册一次 */
const setupWidthRangeListener = () => {
  if (isWidthListenerActive) return

  window.addEventListener('resize', throttledUpdateWidthRange)

  if (!props.widthRange && 'ResizeObserver' in window) {
    const pageContainer = document.querySelector('.app-wrapper') || document.querySelector('#app')
    if (pageContainer) {
      resizeObserver = new ResizeObserver(throttledUpdateWidthRange)
      resizeObserver.observe(pageContainer)
    }
  }

  isWidthListenerActive = true
}

/** 移除监听并释放 ResizeObserver / rAF 资源 */
const teardownWidthRangeListener = () => {
  if (!isWidthListenerActive) return

  window.removeEventListener('resize', throttledUpdateWidthRange)
  resizeObserver?.disconnect()
  resizeObserver = null
  clearResizeThrottle()
  isWidthListenerActive = false
}

/** 根据 resolvedWidthRange 计算容器 fixed 定位样式 */
const containerStyle = computed(() => {
  const { min, max } = resolvedWidthRange.value

  return {
    left: `${min}px`,
    width: `${Math.max(0, max - min)}px`,
  }
})

/**
 * 开始红包雨：注册宽度监听并初始化固定池。
 * 若 envelopePool 已有数据则跳过，避免重复创建 DOM。
 */
const startRedEnvelopeRain = () => {
  if (envelopePool.value.length) return

  setupWidthRangeListener()
  initEnvelopePool()
}

/**
 * 停止红包雨：清空红包池并解绑所有尺寸监听。
 * 配合 startRedEnvelopeRain 可手动控制动画启停。
 */
const stopRedEnvelopeRain = () => {
  envelopePool.value = []
  teardownWidthRangeListener()
}

/* -------------------------------------------------------------------------- */
/* 生命周期 */
/* -------------------------------------------------------------------------- */

/** 初始化宽度范围；nextTick 后启动红包雨，确保容器已渲染并可读取 clientWidth */
onMounted(() => {
  updateWidthRange()

  nextTick(() => {
    startRedEnvelopeRain()
  })
})

/** 组件卸载时清理动画和监听，避免内存泄漏 */
onUnmounted(() => {
  stopRedEnvelopeRain()
})

/** 外部 widthRange 变化时重新计算红包生成区域 */
watch(() => props.widthRange, updateWidthRange, { deep: true })

/** 向父组件暴露开始 / 停止方法，方便手动控制 */
defineExpose({
  startRedEnvelopeRain,
  stopRedEnvelopeRain,
})
</script>

<style lang="less" scoped>
/* -------------------------------------------------------------------------- */
/* 红包雨容器 */
/* -------------------------------------------------------------------------- */
.red-envelope-rain {
  /* 固定定位覆盖页面指定区域，overflow 裁剪超出边界的红包 */
  position: fixed;
  top: 0;
  bottom: 0;
  overflow: hidden;
  /* 不拦截点击，避免挡住下层弹窗和按钮 */
  pointer-events: none;
  z-index: 10;
}

/* -------------------------------------------------------------------------- */
/* 单个红包 */
/* -------------------------------------------------------------------------- */
.rain-envelope {
  position: absolute;
  top: -80px;
  width: 48px;
  height: auto;
  user-select: none;
  /* 提示浏览器优化 transform / opacity，减少动画卡顿 */
  will-change: transform, opacity;
  /* CSS 无限循环，配合固定池避免 JS 持续创建 DOM */
  animation: red-envelope-fall 4s linear infinite;
}

/* -------------------------------------------------------------------------- */
/* 下落动画：translateY 下落 / rotateZ 统一旋转 / opacity 透明度变化 */
/* -------------------------------------------------------------------------- */
@keyframes red-envelope-fall {
  from {
    opacity: var(--start-opacity);
    transform: translateY(0) rotateZ(0deg);
  }

  to {
    opacity: 0.6;
    transform: translateY(110vh) rotateZ(540deg);
  }
}
</style>
